/**
 * /traces — Trace Viewer (auth-gated).
 * =====================================
 * List of captured traces. Click a row → waterfall view of every step.
 *
 * Includes:
 *   • Filter bar (status, min cost, min latency)
 *   • Aggregate stats (p50/p95/p99 latency, total cost, error rate)
 *   • Waterfall view showing each step as a horizontal bar (LLM, tool, chain…)
 *   • Per-step expandable input/output
 *   • Quick "Copy SDK snippet" CTA so devs can wire up tracing in 60s
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Activity, ArrowLeft, AlertCircle, Code, Copy, Check, Trash2, Filter, ChevronRight, Zap, DollarSign, Clock, Cpu, Wrench, Database, GitMerge, ThumbsUp, ThumbsDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtCost = (n) => n >= 0.01 ? `$${n.toFixed(4)}` : `$${(n || 0).toFixed(6)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

const STEP_ICON = {
  llm: <Cpu className="w-3.5 h-3.5 text-emerald-400" />,
  tool: <Wrench className="w-3.5 h-3.5 text-cyan-400" />,
  chain: <GitMerge className="w-3.5 h-3.5 text-teal-400" />,
  retrieval: <Database className="w-3.5 h-3.5 text-amber-400" />,
  embedding: <Database className="w-3.5 h-3.5 text-amber-400" />,
  agent: <Activity className="w-3.5 h-3.5 text-rose-400" />,
  custom: <ChevronRight className="w-3.5 h-3.5 text-slate-400" />,
};

// ─────── List view ───────
const TraceList = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', min_cost_usd: '', min_latency_ms: '' });
  const [sdkOpen, setSdkOpen] = useState(false);
  const [sdkCode, setSdkCode] = useState({ python: '', javascript: '', curl: '' });
  const [sdkLang, setSdkLang] = useState('python');
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.status) qs.set('status', filters.status);
      if (filters.min_cost_usd) qs.set('min_cost_usd', filters.min_cost_usd);
      if (filters.min_latency_ms) qs.set('min_latency_ms', filters.min_latency_ms);
      const [traces, summary] = await Promise.all([
        axios.get(`${API}/traces/?${qs.toString()}`, auth()),
        axios.get(`${API}/traces/stats/summary`, auth()).catch(() => ({ data: null })),
      ]);
      setItems(traces.data.items || []);
      setStats(summary.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadSdk = async () => {
    try {
      const langs = ['python', 'javascript', 'curl'];
      const snippets = await Promise.all(langs.map((l) =>
        axios.get(`${API}/traces/sdk/snippet?language=${l}`, auth()).then((r) => [l, r.data.code]),
      ));
      const dict = Object.fromEntries(snippets);
      setSdkCode(dict);
      setSdkOpen(true);
    } catch { toast.error('Failed to load SDK'); }
  };

  const copy = () => {
    navigator.clipboard.writeText(sdkCode[sdkLang] || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this trace?')) return;
    await axios.delete(`${API}/traces/${id}`, auth()).catch(() => {});
    load();
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="traces-list-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/prompt-playground" className="text-slate-600 hover:text-slate-900">Playground</Link>
            <Link to="/prompts" className="text-slate-600 hover:text-slate-900">Prompts</Link>
            <Link to="/evals" className="text-slate-600 hover:text-slate-900">Evals</Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
              <Activity className="w-3 h-3 mr-1.5 inline" /> Trace observability
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight" data-testid="traces-h1">Traces</h1>
            <p className="text-slate-600 mt-1">Every LLM call, tool call and chain step — step-by-step replay.</p>
          </div>
          <Button variant="outline" className="border-slate-700 text-slate-700 hover:bg-slate-800" onClick={loadSdk} data-testid="traces-sdk-btn">
            <Code className="w-4 h-4 mr-2" /> SDK snippet
          </Button>
        </div>

        {/* Stats strip */}
        {stats && (
          <div className="grid sm:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Traces (24h)', value: stats.trace_count, icon: <Activity className="w-3.5 h-3.5" /> },
              { label: 'Total cost', value: fmtCost(stats.total_cost_usd), icon: <DollarSign className="w-3.5 h-3.5" /> },
              { label: 'p95 latency', value: `${stats.p95_latency_ms}ms`, icon: <Zap className="w-3.5 h-3.5" /> },
              { label: 'Error rate', value: `${(stats.error_rate * 100).toFixed(1)}%`, icon: <AlertCircle className="w-3.5 h-3.5" /> },
            ].map((s, i) => (
              <Card key={i} className="bg-white border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{s.icon}{s.label}</div>
                  <div className="text-xl font-bold text-emerald-300 mt-1 font-mono">{s.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Filters */}
        <Card className="bg-white border-slate-200 shadow-sm mb-6">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <Filter className="w-4 h-4 text-slate-500" />
            <Input placeholder="Status (success/error)" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="bg-white border-slate-300 w-44 text-sm" data-testid="traces-filter-status" />
            <Input placeholder="Min cost USD" value={filters.min_cost_usd} onChange={(e) => setFilters({ ...filters, min_cost_usd: e.target.value })} className="bg-white border-slate-300 w-36 text-sm" />
            <Input placeholder="Min latency ms" value={filters.min_latency_ms} onChange={(e) => setFilters({ ...filters, min_latency_ms: e.target.value })} className="bg-white border-slate-300 w-36 text-sm" />
            <Button size="sm" onClick={load} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="traces-apply-filter">Apply</Button>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <p className="text-slate-500 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm border-dashed">
            <CardContent className="p-12 text-center">
              <Activity className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-600">No traces yet.</p>
              <p className="text-slate-500 text-sm mt-1">Use the SDK snippet to start instrumenting.</p>
              <Button className="mt-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" onClick={loadSdk} data-testid="traces-empty-sdk-btn">
                <Code className="w-4 h-4 mr-2" /> Get SDK snippet
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="traces-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Name</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Status</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Steps</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Tokens</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Cost</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Latency</th>
                    <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-slate-600 font-bold">When</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((t) => (
                    <tr key={t.trace_id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`trace-row-${t.trace_id}`}>
                      <td className="px-4 py-3"><Link to={`/traces/${t.trace_id}`} className="text-slate-900 hover:text-emerald-700 font-medium">{t.name}</Link></td>
                      <td className="px-4 py-3">
                        {t.status === 'error'
                          ? <Badge className="bg-rose-500/15 border-rose-500/40 text-rose-300 px-1.5 py-0 text-[10px]">error</Badge>
                          : <Badge className="bg-emerald-500/15 border-emerald-500/30 text-emerald-300 px-1.5 py-0 text-[10px]">success</Badge>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-[12px]">{t.step_count}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-[12px]">{t.total_tokens}</td>
                      <td className="px-4 py-3 text-emerald-300 font-mono text-[12px]">{fmtCost(t.total_cost_usd)}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono text-[12px]">{t.total_latency_ms}ms</td>
                      <td className="px-4 py-3 text-slate-500 text-[11px]">{fmtDate(t.created_at)}</td>
                      <td className="px-4 py-3"><button onClick={() => onDelete(t.trace_id)} className="text-slate-600 hover:text-rose-400" data-testid={`trace-delete-${t.trace_id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* SDK modal */}
        <Dialog open={sdkOpen} onOpenChange={setSdkOpen}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl">
            <DialogHeader><DialogTitle>SDK snippet · drop into your code in 60s</DialogTitle></DialogHeader>
            <div className="flex gap-2 mb-2">
              {['python', 'javascript', 'curl'].map((l) => (
                <Button key={l} size="sm" variant={sdkLang === l ? 'default' : 'outline'} className={sdkLang === l ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400' : 'border-slate-700 text-slate-300'} onClick={() => setSdkLang(l)} data-testid={`sdk-tab-${l}`}>{l}</Button>
              ))}
              <Button size="sm" variant="ghost" className="ml-auto text-slate-600" onClick={copy} data-testid="sdk-copy-btn">
                {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <pre className="text-xs bg-slate-950 p-4 rounded border border-slate-800 overflow-auto max-h-[60vh] whitespace-pre-wrap text-emerald-100 font-mono">{sdkCode[sdkLang] || 'Loading…'}</pre>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// ─────── Detail / Waterfall view ───────
const TraceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [feedback, setFeedback] = useState(null); // 'resolved' | 'unresolved' | null
  const [submittingFb, setSubmittingFb] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/traces/${id}`, auth());
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/login');
      else toast.error('Trace not found');
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  // v33 — Reasoning Efficiency feedback chips
  const sendFeedback = async (resolved) => {
    setSubmittingFb(true);
    try {
      await axios.post(`${API}/reasoning-efficiency/feedback`, {
        trace_id: id,
        resolved,
        feature_slug: data?.trace?.name || null,
      }, auth());
      setFeedback(resolved ? 'resolved' : 'unresolved');
      toast.success(
        <div>
          <div className="font-semibold">Feedback recorded</div>
          <div className="text-xs mt-0.5">{resolved ? 'Counted as a resolved intent.' : 'Counted as loop-waste.'} Updates the Reasoning Efficiency dashboard.</div>
        </div>
      );
    } catch (e) {
      toast.error('Could not record feedback');
    } finally {
      setSubmittingFb(false);
    }
  };

  const maxLat = useMemo(
    () => Math.max(1, ...(data?.steps || []).map((s) => s.latency_ms || 0)),
    [data],
  );

  if (!data) return <div className="min-h-screen bg-white text-slate-600 p-10">Loading…</div>;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="trace-detail-page">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/traces" className="flex items-center gap-2 text-slate-600 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Traces</Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{data.trace.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
              <span>{fmtDate(data.trace.started_at)}</span>
              <span>·</span>
              <span>steps: <span className="text-slate-700 font-mono">{data.trace.step_count}</span></span>
              <span>·</span>
              <span>tokens: <span className="text-slate-700 font-mono">{data.trace.total_tokens}</span></span>
              <span>·</span>
              <span>cost: <span className="text-emerald-300 font-mono">{fmtCost(data.trace.total_cost_usd)}</span></span>
              <span>·</span>
              <span>latency: <span className="text-slate-700 font-mono">{data.trace.total_latency_ms}ms</span></span>
            </div>
          </div>
          {/* v33 — Reasoning Efficiency feedback chips */}
          <div className="flex items-center gap-2" data-testid="trace-feedback-chips">
            <span className="text-xs text-slate-500 mr-1">Did this trace resolve the intent?</span>
            <button
              onClick={() => sendFeedback(true)}
              disabled={submittingFb || feedback === 'resolved'}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${feedback === 'resolved' ? 'bg-emerald-500 text-slate-950' : 'bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100'}`}
              data-testid="trace-thumbs-up"
            >
              <ThumbsUp className="w-3.5 h-3.5" /> Resolved
            </button>
            <button
              onClick={() => sendFeedback(false)}
              disabled={submittingFb || feedback === 'unresolved'}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${feedback === 'unresolved' ? 'bg-rose-500 text-white' : 'bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100'}`}
              data-testid="trace-thumbs-down"
            >
              <ThumbsDown className="w-3.5 h-3.5" /> Loop waste
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-1.5" data-testid="trace-waterfall">
          {(data.steps || []).map((s, i) => {
            const pct = Math.max(2, Math.round(((s.latency_ms || 0) / maxLat) * 100));
            const isExp = !!expanded[s.step_id];
            return (
              <Card key={s.step_id || i} className="bg-white border-slate-200 shadow-sm hover:border-slate-700" data-testid={`trace-step-${i}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded({ ...expanded, [s.step_id]: !isExp })}>
                    <div className="flex items-center gap-1.5 w-44 shrink-0">
                      {STEP_ICON[s.type] || STEP_ICON.custom}
                      <span className="text-sm font-medium text-slate-900 truncate">{s.name}</span>
                    </div>
                    <div className="flex-1 h-5 bg-slate-950 text-slate-100 rounded relative overflow-hidden">
                      <div
                        className={`h-full ${s.status === 'error' ? 'bg-rose-500/40' : 'bg-emerald-500/40'} rounded`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-400 font-mono w-56 shrink-0 justify-end">
                      {s.model && <Badge className="bg-slate-100 border-slate-300 text-slate-700 px-1.5 py-0 text-[9px]">{s.model}</Badge>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{s.latency_ms}ms</span>
                      <span className="text-emerald-300">{fmtCost(s.cost_usd)}</span>
                    </div>
                  </div>
                  {isExp && (
                    <div className="mt-3 pt-3 border-t border-slate-800 space-y-2 text-xs">
                      {s.input && <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Input</div><pre className="bg-slate-950 p-2 rounded border border-slate-800 whitespace-pre-wrap text-slate-300 font-mono">{typeof s.input === 'string' ? s.input : JSON.stringify(s.input, null, 2)}</pre></div>}
                      {s.output && <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Output</div><pre className="bg-slate-950 p-2 rounded border border-slate-800 whitespace-pre-wrap text-emerald-200 font-mono">{typeof s.output === 'string' ? s.output : JSON.stringify(s.output, null, 2)}</pre></div>}
                      {s.error && <div className="text-rose-300 font-mono">{s.error}</div>}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

const TraceViewer = () => {
  const { id } = useParams();
  return id ? <TraceDetail /> : <TraceList />;
};

export default TraceViewer;
